import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { ExternalLink, ArrowRight, Building2, Briefcase, Globe } from 'lucide-react'
import ecosystem from '../data/ecosystem.json'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
}

const colorClasses = {
  primary: 'from-primary-500 to-primary-700',
  blue: 'from-blue-500 to-blue-700',
  emerald: 'from-emerald-500 to-emerald-700',
}

const bgColorClasses = {
  primary: 'bg-primary-50 text-primary-700',
  blue: 'bg-blue-50 text-blue-700',
  emerald: 'bg-emerald-50 text-emerald-700',
}

export default function Ecosystem() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="ecosystem" className="relative py-20 overflow-hidden bg-gray-50 lg:py-32">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-primary-100 text-primary-700">
              <Building2 className="w-4 h-4 mr-1.5" />
              Écosystème
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Le Groupe <span className="gradient-text">Octego</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {ecosystem.holding.description}
            </p>
          </motion.div>

          {/* Holding Card */}
          <div className="relative max-w-4xl mx-auto mt-12 flex flex-col items-center">
            <motion.div
              variants={itemVariants}
              className="w-full"
            >
              <motion.a
                href={ecosystem.holding.website}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.01 }}
                className="relative block p-8 overflow-hidden transition-shadow bg-white border border-gray-100 shadow-lg group rounded-3xl hover:shadow-2xl"
              >
                <div className="absolute top-0 right-0 w-64 h-64 translate-x-32 -translate-y-32 rounded-full bg-gradient-to-br from-primary-100 to-rose-valley-100 blur-3xl opacity-50" />
                <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center">
                  <div className={`flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${colorClasses[ecosystem.holding.color]} p-3`}>
                    {ecosystem.holding.logo ? (
                      <img 
                        src={ecosystem.holding.logo} 
                        alt={ecosystem.holding.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'block'
                        }}
                      />
                    ) : null}
                    <span className={`text-3xl font-bold text-white font-display ${ecosystem.holding.logo ? 'hidden' : ''}`}>O</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-2xl font-bold text-gray-900">{ecosystem.holding.name}</h3>
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {ecosystem.holding.type}
                      </span>
                    </div>
                    <p className="mt-2 text-gray-600">{ecosystem.holding.tagline}</p>
                    <p className="mt-1 text-sm text-primary-600 font-medium">{ecosystem.holding.role}</p>
                  </div>
                  {/*
                  <div className="flex items-center gap-2 text-gray-400 transition-colors group-hover:text-primary-600">
                    <span className="text-sm font-medium">Visiter</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                  */}
                </div>
              </motion.a>
            </motion.div>
            {/* Flèches SVG design vers les filiales */}
            <div className="relative w-full flex justify-center" style={{ height: 60 }}>
              {/* Flèche vers Scaltify (gauche) - droite et élégante */}
              <svg className="absolute left-1/3 top-0" width="80" height="44" viewBox="0 0 80 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{overflow: 'visible'}}>
                <line x1="40" y1="8" x2="16" y2="40" stroke="#3B82F6" strokeWidth="2.5" markerEnd="url(#arrowhead1)" />
                <defs>
                  <marker id="arrowhead1" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
                    <polygon points="0 0, 10 5, 0 10" fill="#3B82F6" />
                  </marker>
                </defs>
              </svg>
              {/* Flèche vers Scalt (droite) - droite et élégante */}
              <svg className="absolute right-1/3 top-0" width="80" height="44" viewBox="0 0 80 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{overflow: 'visible'}}>
                <line x1="40" y1="8" x2="64" y2="40" stroke="#10B981" strokeWidth="2.5" markerEnd="url(#arrowhead2)" />
                <defs>
                  <marker id="arrowhead2" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
                    <polygon points="0 0, 10 5, 0 10" fill="#10B981" />
                  </marker>
                </defs>
              </svg>
            </div>
          </div>

          {/* Companies Grid */}
          <motion.div
            variants={containerVariants}
            className="grid gap-6 mt-12 md:grid-cols-2"
          >
            {ecosystem.companies.map((company) => (
              <motion.div
                key={company.id}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className="relative p-6 overflow-hidden transition-shadow bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-xl"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 translate-x-16 -translate-y-16 rounded-full bg-gradient-to-br ${colorClasses[company.color]} opacity-10 blur-2xl`} />
                
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-white border border-gray-100 shadow-sm p-2.5">
                      {company.logo ? (
                        <img 
                          src={company.logo} 
                          alt={company.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                      ) : null}
                      <Briefcase className={`w-7 h-7 text-gray-600 ${company.logo ? 'hidden' : ''}`} />
                    </div>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${bgColorClasses[company.color]}`}>
                      {company.type}
                    </span>
                  </div>

                  <h3 className="mt-4 text-xl font-bold text-gray-900">{company.name}</h3>
                  {company.parentCompany && (
                    <p className="text-sm text-gray-500">Filiale de {company.parentCompany}</p>
                  )}
                  <p className="mt-1 font-medium text-primary-600">{company.role}</p>
                  
                  <p className="mt-3 text-sm text-gray-600 line-clamp-3">{company.description}</p>

                  {/* Services Tags */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {company.services.slice(0, 3).map((service) => (
                      <span
                        key={service}
                        className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600"
                      >
                        {service}
                      </span>
                    ))}
                    {company.services.length > 3 && (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                        +{company.services.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-4 pt-4 mt-4 border-t border-gray-100">
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-primary-600"
                    >
                      <Globe className="w-4 h-4" />
                      Site web
                    </a>
                    {company.blog && (
                      <a
                        href={company.blog}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-primary-600"
                      >
                        <ArrowRight className="w-4 h-4" />
                        Blog
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
